import {
  DEFAULT_REACTIONS,
  type ReactionOption,
} from "@somethings-ui/web";
import { Loader2, MessageCircleMore } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { AttentionThreadSkeleton } from "./attention-thread-skeleton";
import {
  AttentionLightbox,
  type AttentionLightboxMedia,
} from "./attention-lightbox";
import type { AttentionSurfaceCapabilities } from "./attention-capabilities";
import {
  AttentionRowView,
  type AttentionSpecialContentRenderers,
  type AttentionThreadCallbacks,
} from "./attention-thread-row";
import type { AttentionView } from "./attention-view";
import { useAttentionThreadScroll } from "./use-attention-thread-scroll";

export type {
  AttentionSpecialContentRenderers,
  AttentionThreadCallbacks,
} from "./attention-thread-row";

export interface AttentionThreadProps extends AttentionThreadCallbacks {
  view: AttentionView;
  capabilities: AttentionSurfaceCapabilities;
  reactionChoices?: ReactionOption[];
  isLoading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  emptyLabel?: string;
  specialContent?: AttentionSpecialContentRenderers;
}

export function AttentionThread({
  view,
  capabilities,
  reactionChoices = DEFAULT_REACTIONS,
  isLoading = false,
  error = null,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  emptyLabel = "No messages yet",
  onReply,
  onToggleReaction,
  onCopy,
  specialContent,
}: AttentionThreadProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [lightboxMedia, setLightboxMedia] =
    useState<AttentionLightboxMedia | null>(null);
  const rowCount = view.rows.length;

  const getRowKey = useCallback(
    (index: number) => view.rows[index].key,
    [view.rows],
  );

  const {
    scrollRef,
    topSentinelRef,
    virtualizer,
    handleScroll,
    handleMediaSettled,
  } = useAttentionThreadScroll({
    rowCount,
    getRowKey,
    hasMore,
    isLoadingMore,
    onLoadMore,
  });

  if (error !== null) {
    return (
      <ThreadShell>
        <p className="m-auto font-body text-body-3 text-states-error">{error}</p>
      </ThreadShell>
    );
  }

  if (isLoading && rowCount === 0) {
    return <AttentionThreadSkeleton />;
  }

  if (rowCount === 0) {
    return (
      <ThreadShell>
        <div className="m-auto flex max-w-[240px] flex-col items-center gap-3 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-surfaces-z2">
            <MessageCircleMore
              className="size-6 text-icon-tertiary"
              aria-hidden
            />
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-heading text-header-5 text-text-primary">
              {emptyLabel}
            </p>
            <p className="font-body text-body-3 text-text-tertiary">
              {capabilities.canCompose
                ? "Send a message to start the conversation."
                : "Messages will appear here once the conversation begins."}
            </p>
          </div>
        </div>
      </ThreadShell>
    );
  }

  return (
    <>
      {lightboxMedia ? (
        <AttentionLightbox
          media={lightboxMedia}
          onClose={() => setLightboxMedia(null)}
        />
      ) : null}
      <div
        ref={scrollRef}
        role="log"
        aria-label="Message thread"
        onScroll={handleScroll}
        className="subtle-scrollbar relative min-w-0 flex-1 overflow-y-auto"
      >
        <div
          ref={topSentinelRef}
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
        />
        {isLoadingMore ? (
          <div
            className="absolute inset-x-0 top-0 z-10 flex justify-center py-1"
            aria-live="polite"
          >
            <Loader2
              className="size-4 animate-spin text-text-tertiary"
              aria-label="Loading earlier messages"
            />
          </div>
        ) : null}

        <div
          className="relative w-full"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="absolute inset-x-0 top-0 px-4 pb-4"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              <AttentionRowView
                row={view.rows[virtualRow.index]}
                capabilities={capabilities}
                reactionChoices={reactionChoices}
                openId={openId}
                setOpenId={setOpenId}
                onReply={onReply}
                onToggleReaction={onToggleReaction}
                onCopy={onCopy}
                onMediaSettled={handleMediaSettled}
                onOpenMedia={setLightboxMedia}
                specialContent={specialContent}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function ThreadShell({ children }: { children: ReactNode }) {
  return (
    <div
      role="log"
      aria-label="Message thread"
      className="subtle-scrollbar flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
    >
      {children}
    </div>
  );
}
